import { useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { onStartProject } = useOutletContext();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/.+@.+\..+/.test(form.email) || !form.message) {
      toast.error("Please fill all fields.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received.");
      setForm({ name: "", email: "", message: "" });
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail
        : "Could not send. Try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-40 md:pt-56 pb-24 md:pb-40" data-testid="contact-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">
          [ Vol. IV ] · Contact
        </p>
        <h1 className="mt-8 font-display text-6xl md:text-[11rem] leading-[0.86] tracking-tightest">
          Say <em className="italic text-stone-muted">hello</em>.
        </h1>

        <div className="mt-16 grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="col-span-12 md:col-span-4 space-y-10">
            <ContactBlock label="Direct" value="hello@bosecrafts.com" href="mailto:hello@bosecrafts.com" />
            <ContactBlock label="Studio" value={"Sevoke Road\nSiliguri, WB 734001\nIndia"} />
            <ContactBlock label="Availability" value={"Currently — Q3 2026\nNext window — Q1 2027"} />
            <ContactBlock
              label="A larger question?"
              value="Use the multi-step brief — it helps us respond faster."
              cta={
                <button
                  onClick={onStartProject}
                  data-testid="contact-start-project"
                  className="mt-3 border-b border-charcoal pb-1 text-[11px] uppercase tracking-[0.24em] hover:border-transparent transition-colors"
                >
                  Start a project →
                </button>
              }
            />
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="col-span-12 md:col-span-7 md:col-start-6 space-y-10"
            data-testid="contact-form"
          >
            <FieldGroup label="01 — Your name">
              <input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Founder"
                className="w-full bg-transparent border-b border-charcoal/30 focus:border-charcoal outline-none py-3 font-serif text-2xl md:text-3xl placeholder:text-stone-soft"
              />
            </FieldGroup>
            <FieldGroup label="02 — Your email">
              <input
                data-testid="contact-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@company.com"
                className="w-full bg-transparent border-b border-charcoal/30 focus:border-charcoal outline-none py-3 font-serif text-2xl md:text-3xl placeholder:text-stone-soft"
              />
            </FieldGroup>
            <FieldGroup label="03 — Message">
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us what you're building and how we can help."
                rows={5}
                className="w-full bg-transparent border-b border-charcoal/30 focus:border-charcoal outline-none py-3 font-serif text-xl resize-none placeholder:text-stone-soft"
              />
            </FieldGroup>

            <div className="flex items-center justify-between pt-8">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-stone-muted">
                {sent ? "Message received. Thank you." : "We reply within 48 hours."}
              </span>
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="border border-charcoal bg-charcoal text-ivory disabled:bg-stone-soft disabled:border-stone-soft px-7 py-4 text-[11px] uppercase tracking-[0.22em] hover:bg-ivory hover:text-charcoal transition-colors"
              >
                {busy ? "Sending…" : "Send message ↗"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-stone-muted mb-3">
        {label}
      </span>
      {children}
    </div>
  );
}

function ContactBlock({ label, value, href, cta }) {
  return (
    <div className="border-t border-charcoal/25 pt-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-muted mb-3">{label}</p>
      {href ? (
        <a href={href} className="u-link font-serif text-xl">{value}</a>
      ) : (
        <p className="font-serif text-lg whitespace-pre-line leading-relaxed">{value}</p>
      )}
      {cta}
    </div>
  );
}
